import {
  useEffect,
  useRef,
  useState
} from 'react'
import {
  authService,
  getApiErrorMessage
} from '../services/api'
import type {
  AssistantCharacter,
  CustomCharacterDetails,
  PersonalityTraits,
  VoiceOption
} from '../services/api'
import type {
  LucideIcon
} from 'lucide-react'
import {
  ClipboardPaste,
  Copy,
  Lightbulb,
  ListChecks,
  MessageCircle,
  Handshake,
  Heart,
  Loader2,
  Play,
  Sparkles,
  Square,
  X
} from 'lucide-react'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  clearCustomCharacterDraft,
  loadCustomCharacterDraft,
  saveCustomCharacterDraft,
} from '../utils/customCharacterDraft'
import {
  BIO_MAX_LENGTH,
  LEVEL_VALUES,
  NAME_MAX_LENGTH,
  TRAIT_LEVELS,
  valueToLevel,
} from '../utils/customCharacterTraits'
import {
  buildCharacterSuggestionPrompt,
  parseCharacterSuggestion,
} from '../utils/customCharacterSuggestion'
import {
  copyTextToClipboard
} from '../utils/clipboard'
import {
  AssistantPictureField
} from './AssistantPictureField'
import {
  FeatureFlags
} from '../analytics/posthog'
import {
  useFeatureFlagEnabled
} from 'posthog-js/react'

const TRAIT_CONFIG: { key: keyof PersonalityTraits; label: string; icon: LucideIcon }[] = [
  { key: 'openness', label: 'Openness', icon: Lightbulb },
  { key: 'conscientiousness', label: 'Conscientiousness', icon: ListChecks },
  { key: 'extraversion', label: 'Extraversion', icon: MessageCircle },
  { key: 'agreeableness', label: 'Agreeableness', icon: Handshake },
  { key: 'sensitivity', label: 'Sensitivity', icon: Heart },
]

const DEFAULT_TRAITS: PersonalityTraits = {
  openness: 50,
  conscientiousness: 50,
  extraversion: 50,
  agreeableness: 50,
  sensitivity: 50,
}

interface CustomCharacterFormProps {
  onSuccess: (character: AssistantCharacter) => void
  onClose: () => void
  existingCharacter?: AssistantCharacter | null | undefined
  /**
   * Called when the picture changes without the form being submitted — the
   * upload and revert land immediately, so the parent has to take the updated
   * character (and poll for the generated one after a revert).
   */
  onPictureChanged?: ((character: AssistantCharacter) => void) | undefined
  /**
   * Take the details instead of saving them, for a visitor with no account yet
   * (GET-18726). The draft is kept, so the wizard can re-open the form filled
   * in if saving after signup fails.
   */
  onSubmitDetails?: ((details: CustomCharacterDetails) => void) | undefined
}

export function CustomCharacterForm({
  onSuccess,
  onClose,
  existingCharacter,
  onPictureChanged,
  onSubmitDetails,
}: CustomCharacterFormProps): JSX.Element {
  // Restore any in-progress edits from a previous, un-submitted session so the
  // user resumes where they left off. Computed once on mount (lazy initializer)
  // since the modal is remounted each time it opens.
  const [initialDraft] = useState(() =>
    loadCustomCharacterDraft(existingCharacter?.id ?? null),
  )

  // Snapshot of the values the form opened with, so the mirror effect can skip
  // persisting an untouched session (which would otherwise write a pristine
  // draft equal to the server character that then wins on the next open). Voice
  // is tracked separately (voiceEdited) because the picker's async fallback can
  // change selectedVoice without any user action.
  const [initialValues] = useState(() => ({
    name: initialDraft?.name ?? existingCharacter?.name ?? '',
    bio: initialDraft?.bio ?? existingCharacter?.bio ?? '',
    traits: initialDraft?.traits ?? existingCharacter?.personality_traits ?? DEFAULT_TRAITS,
  }))

  const [name, setName] = useState(
    initialDraft?.name ?? existingCharacter?.name ?? '',
  )
  const [bio, setBio] = useState(initialDraft?.bio ?? existingCharacter?.bio ?? '')
  const [traits, setTraits] = useState<PersonalityTraits>(
    initialDraft?.traits ?? existingCharacter?.personality_traits ?? { ...DEFAULT_TRAITS },
  )
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The voice can only be changed on an already-created custom character; on
  // first creation the backend auto-selects one matching the persona's gender.
  const [voices, setVoices] = useState<VoiceOption[]>([])
  const [selectedVoice, setSelectedVoice] = useState(
    initialDraft?.selectedVoice ?? existingCharacter?.selected_voice ?? '',
  )
  // Whether the user picked a voice themselves (vs. the effect's async fallback),
  // so the dirty check counts only real voice edits.
  const [voiceEdited, setVoiceEdited] = useState(false)
  const [previewPlaying, setPreviewPlaying] = useState(false)

  const canUploadPicture = useFeatureFlagEnabled(
    FeatureFlags.CUSTOM_PROFILE_PICTURE_UPLOAD,
  )
  // Surfaced when playback fails. Before GET-10702 a failure was only logged to
  // the console, so a browser-blocked preview was indistinguishable from a dead
  // button — which is how it went unreported for two months.
  const [previewFailed, setPreviewFailed] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const previewUrl = voices.find((v) => v.name === selectedVoice)?.preview_url

  const handlePreview = (): void => {
    const audio = audioRef.current
    if (!audio || !previewUrl) return
    // Cleared on every entry, not just the play branch, so no path can leave a
    // stale error on screen.
    setPreviewFailed(false)
    if (previewPlaying) {
      audio.pause()
      audio.currentTime = 0
      setPreviewPlaying(false)
      return
    }
    audio.src = previewUrl
    void audio.play().then(
      () => setPreviewPlaying(true),
      (err) => {
        console.error('Failed to play voice preview:', err)
        setPreviewPlaying(false)
        setPreviewFailed(true)
      },
    )
  }

  // Stop playback when the user switches to a different voice.
  useEffect(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.currentTime = 0
    }
    setPreviewPlaying(false)
    setPreviewFailed(false)
  }, [selectedVoice])

  useEffect(() => {
    if (!existingCharacter) return
    let cancelled = false
    void authService
      .listCharacterVoices()
      .then((list) => {
        if (cancelled) return
        setVoices(list)
        // Keep the picker in sync with the available options: preserve the
        // current selection (from the saved character or a restored draft) when
        // it's still offered, otherwise fall back to the first option.
        setSelectedVoice((current) =>
          current && list.some((v) => v.name === current)
            ? current
            : (list[0]?.name ?? ''),
        )
      })
      .catch((err) => {
        console.error('Failed to load voices:', err)
      })
    return () => {
      cancelled = true
    }
  }, [existingCharacter])

  // Mirror in-progress edits to storage so an interrupted session can be
  // resumed. Only writes once a field actually differs from what the form
  // opened with, so an untouched session never persists a pristine draft. The
  // draft is cleared only on a successful save.
  useEffect(() => {
    const isDirty =
      name !== initialValues.name ||
      bio !== initialValues.bio ||
      voiceEdited ||
      TRAIT_CONFIG.some(({ key }) => traits[key] !== initialValues.traits[key])
    if (!isDirty) return
    saveCustomCharacterDraft(existingCharacter?.id ?? null, {
      name,
      bio,
      traits,
      selectedVoice,
    })
  }, [existingCharacter?.id, name, bio, traits, selectedVoice, voiceEdited, initialValues])

  // "Ask your AI" shortcut: copy a prompt for ChatGPT/Claude, paste the JSON
  // it answers with, and the form fills itself. Create-only — an edit already
  // has a character to start from.
  const [promptCopied, setPromptCopied] = useState(false)
  const [pasteOpen, setPasteOpen] = useState(false)
  const [pasteText, setPasteText] = useState('')
  const [pasteError, setPasteError] = useState<string | null>(null)
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current)
  }, [])

  const handleCopyPrompt = async (): Promise<void> => {
    setPasteError(null)
    // The paste box opens on both outcomes so a failed copy has somewhere to
    // show its error — otherwise a blocked clipboard leaves the button looking
    // dead, the GET-10702 class this file already guards against in previews.
    setPasteOpen(true)
    try {
      await copyTextToClipboard(buildCharacterSuggestionPrompt())
      trackEvent(OnboardingEvents.ASSISTANT_CUSTOM_PROMPT_COPIED)
      setPromptCopied(true)
      copiedTimerRef.current = setTimeout(() => setPromptCopied(false), 2000)
    } catch {
      setPasteError("Couldn't copy the prompt. Try again.")
    }
  }

  const handleApplySuggestion = (): void => {
    const suggestion = parseCharacterSuggestion(pasteText)
    if (!suggestion) {
      setPasteError("That doesn't look like the JSON block. Paste the whole reply, code block included.")
      return
    }
    setName(suggestion.name)
    setBio(suggestion.bio)
    setTraits(suggestion.traits)
    setPasteText('')
    setPasteError(null)
    setPasteOpen(false)
    trackEvent(OnboardingEvents.ASSISTANT_CUSTOM_SUGGESTION_APPLIED)
  }

  const isValid = name.trim().length > 0 && bio.trim().length > 0

  const handleTraitChange = (key: keyof PersonalityTraits, value: number): void => {
    setTraits((prev) => ({ ...prev, [key]: value }))
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (!isValid || submitting) return

    const details = { name: name.trim(), bio: bio.trim(), personality_traits: traits }
    if (onSubmitDetails) {
      onSubmitDetails(details)
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      let character = await authService.createCustomCharacter(details)
      // Persist a voice change only when editing an existing character (the
      // voice picker is hidden on first creation while auto-selection runs).
      if (
        existingCharacter &&
        selectedVoice &&
        selectedVoice !== existingCharacter.selected_voice
      ) {
        character = await authService.updateCustomCharacterVoice(selectedVoice)
      }
      clearCustomCharacterDraft(existingCharacter?.id ?? null)
      trackEvent(OnboardingEvents.ASSISTANT_CUSTOM_SAVED, {
        mode: existingCharacter ? 'update' : 'create',
      })
      onSuccess(character)
    } catch (err) {
      console.error('Failed to create custom character:', err)
      const isRateLimited = (err as { response?: { status?: number } })?.response?.status === 429
      setError(
        isRateLimited
          ? 'Catch can draw 3 new pictures an hour. Try again a little later.'
          : getApiErrorMessage(err, "We couldn't create your character. Please try again."),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = (): void => {
    trackEvent(OnboardingEvents.ASSISTANT_CUSTOM_FORM_CANCELLED, {
      mode: existingCharacter ? 'update' : 'create',
    })
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">
            {existingCharacter ? 'Edit Your Character' : 'Create Your Character'}
          </h2>
          <button
            onClick={handleCancel}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="p-6 space-y-5">
          {!existingCharacter && (
            <div className="rounded-xl border border-primary-100 bg-primary-50 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <Sparkles className="w-4 h-4 text-primary-500 mt-0.5 shrink-0" />
                <div className="text-sm text-gray-700">
                  <p className="font-medium text-gray-900">Not sure where to start?</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Ask ChatGPT or Claude to design your assistant from what it already knows about you, then paste its answer here.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void handleCopyPrompt()}
                  disabled={submitting}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {promptCopied ? 'Copied!' : 'Copy prompt'}
                </button>
                {!pasteOpen && (
                  <button
                    type="button"
                    onClick={() => setPasteOpen(true)}
                    disabled={submitting}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-primary-700 bg-white border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-50"
                  >
                    <ClipboardPaste className="w-3.5 h-3.5" />
                    Paste the reply
                  </button>
                )}
              </div>
              {pasteOpen && (
                <div className="space-y-2">
                  <textarea
                    aria-label="Paste the reply"
                    value={pasteText}
                    onChange={(e) => {
                      setPasteText(e.target.value)
                      setPasteError(null)
                    }}
                    rows={4}
                    placeholder="Paste the JSON reply here..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
                    disabled={submitting}
                  />
                  {pasteError && <p className="text-xs text-red-600">{pasteError}</p>}
                  <button
                    type="button"
                    onClick={handleApplySuggestion}
                    disabled={!pasteText.trim() || submitting}
                    className="px-3 py-2 text-xs font-medium text-primary-700 bg-white border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Fill in the form
                  </button>
                </div>
              )}
            </div>
          )}

          {/* The picture saves the moment it is picked, so it sits beside the
              form rather than in it, and the parent owns the character it
              renders — a revert leaves the row with no picture until the new
              headshot lands, and only the parent's polling sees that arrive. */}
          {canUploadPicture === true && existingCharacter && onPictureChanged && (
            <AssistantPictureField
              character={existingCharacter}
              onChanged={onPictureChanged}
              disabled={submitting}
            />
          )}

          <div>
            <label htmlFor="char-name" className="block text-sm font-medium text-gray-700 mb-1.5">
              Name
            </label>
            <input
              id="char-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={NAME_MAX_LENGTH}
              placeholder="e.g. Alex, Sam, Jordan..."
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
              disabled={submitting}
            />
            <p className="text-xs text-gray-400 mt-1">{name.length}/{NAME_MAX_LENGTH}</p>
          </div>

          <div>
            <label htmlFor="char-bio" className="block text-sm font-medium text-gray-700 mb-1.5">
              Bio
            </label>
            <textarea
              id="char-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={BIO_MAX_LENGTH}
              rows={3}
              placeholder="A short description of your assistant..."
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
              disabled={submitting}
            />
            <p className="text-xs text-gray-400 mt-1">
              Public. People who click your assistant&apos;s name in an email signature see it. {bio.length}/{BIO_MAX_LENGTH}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Personality
            </label>
            <div className="space-y-5">
              {TRAIT_CONFIG.map(({ key, label, icon: Icon }) => {
                const level = valueToLevel(traits[key])
                const levels = TRAIT_LEVELS[key]
                const selected = levels[level]!
                const firstLevel = levels[0]!
                const lastLevel = levels[levels.length - 1]!
                return (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                        <Icon className="w-3.5 h-3.5" />
                        {label}
                      </span>
                      <span className="text-xs font-medium text-primary-600">
                        {selected.label}
                      </span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={25}
                      value={LEVEL_VALUES[level]}
                      onChange={(e) => handleTraitChange(key, Number(e.target.value))}
                      disabled={submitting}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500 disabled:opacity-50"
                    />
                    <div className="flex justify-between mt-0.5">
                      <span className="text-[10px] text-gray-400">{firstLevel.label}</span>
                      <span className="text-[10px] text-gray-400">{lastLevel.label}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 italic">
                      {selected.description}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>

          {existingCharacter && voices.length > 0 && (
            <div>
              <label
                htmlFor="char-voice"
                className="block text-sm font-medium text-gray-700 mb-1.5"
              >
                Voice
              </label>
              <div className="flex items-center gap-2">
                <select
                  id="char-voice"
                  value={selectedVoice}
                  onChange={(e) => {
                    setSelectedVoice(e.target.value)
                    setVoiceEdited(true)
                  }}
                  disabled={submitting}
                  className="flex-1 px-3.5 py-2.5 border border-gray-300 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent disabled:opacity-50"
                >
                  {voices.map((voice) => (
                    <option key={voice.name} value={voice.name}>
                      {voice.label} — {voice.description}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={!previewUrl || submitting}
                  aria-label={previewPlaying ? 'Stop preview' : 'Play preview'}
                  className="shrink-0 p-2.5 text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {previewPlaying ? (
                    <Square className="w-4 h-4" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                </button>
              </div>
              <audio
                ref={audioRef}
                onEnded={() => setPreviewPlaying(false)}
                className="hidden"
              />
              <p className="text-xs text-gray-400 mt-1">
                The voice your assistant uses on phone calls.
              </p>
              {previewFailed && (
                <p className="text-xs text-red-600 mt-1">
                  We couldn&apos;t play that preview. Give it another try.
                </p>
              )}
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || submitting}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-500 rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {existingCharacter ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
